import React from 'react';
import ReactMarkdown from 'react-markdown';


import { IChevronDown, IChevronUp, ITriangleSide, IPreferences } from '~/_base/components/helixIcons';
import Container from '~/micro-services/insights-chat/ioc-container';
import bot from '~/micro-services/insights-chat/bot/bot';
import { apiCallWithOptions } from '~/_base/functions/apicalls';
import { ILoader } from '~/_base/components/helixIcons';
import InsightsChatViewEntry from './insightsChatViewEntry.js';
import { t } from 'i18next';
import { ListItemButton, Chip, Input, InputAdornment, IconButton, Drawer, Menu, MenuItem, Collapse, List } from "@mui/material";

export default class InsightsChatView extends React.Component {
  constructor(props) {
    super();

    Container.register('today-generator', () => new Date(Date.now()));
    Container.register('sessionProvider', this);
    Container.register('fetch', (url, options) =>
      apiCallWithOptions(
        url,
        (options || {}).method || 'GET',
        Object.assign({ responseOnly: true }, options)
      )
    );

    this.hasRemaining = false;
    this.loadingRemaining = false;
    this.state = {
      loading: false,
      actionsOpen: null,
      actionGroupsOpen: {},
      conversation: [],
    };

    let getLexicon = apiCallWithOptions(`/site_assistant/lexicon.json`, 'GET', {
      cacheRequest: true,
    });
    let getChatHistory = apiCallWithOptions(`/site_assistant.json`, 'GET');
    Promise.all([getLexicon, getChatHistory]).then(responses => {
      this.captureNextDetailsFromResponse(responses[1]);
      let messages = responses[1].messages.reverse();
      let chatHistory = messages.map(item => item.message);
      let stateHistory = messages
        .filter(item => item.bot_state != null)
        .map(item => JSON.parse(item.bot_state));

      let lexicon = responses[0];
      lexicon.key = 'ServerDataAggregatesAndTemplateNames';
      this.chatSessionId = bot.startChatSession(null, {
        nlpConfig: lexicon,
      });
      this.botSession = Object.assign(this.botSession, {
        history: stateHistory,
      });

      this.addConversationBubble(chatHistory, 'instant', false, () => {
        // Need to wait for stateUpdate to complete before continuing
        if (this.props.embedded !== true) {
          this.captureSessionContext(this.props.sessionContext);
        }
        if (chatHistory.length == 0) {
          this.issueQueryTextFromKeyboard(t('What can you do?'));
        }
      });
    });
  }

  componentDidMount() {
    if (this.props.embedded) {
      window.ApeInsightsChatViewProxy = {
        pushQuery: queryText => this.issueQueryText(queryText),

        // alternatives: optional array of alternative interpretations of the speech, in the
        // format [{confidence:float, transcript:string}, ...]
        pushQueryFromDictation: (queryText, alternatives) =>
          this.issueQueryText(queryText),
        pushQueryFromKeyboard: queryText =>
          this.issueQueryTextFromKeyboard(queryText),
        pushSessionContext: sessionContext =>
          this.captureSessionContext(sessionContext),
      };
    }
    this.boundChatWindowResized = () => this.chatWindowResized();
    window.addEventListener('resize', this.boundChatWindowResized, {
      passive: true,
    });
  }

  captureSessionContext(sessionContext) {
    if (this.chatSessionId) {
      bot.updateChatSessionContext(this.chatSessionId, sessionContext);
      let tempBubble = {
        owner: 'system',
        text: `All Projects`,
        temp: true,
      };

      if (sessionContext.project) {
        tempBubble = {
          owner: 'system',
          text: `${sessionContext.projectJobNumber} - ${sessionContext.projectName}`,
          temp: true,
        };
      }
      this.addConversationBubble(tempBubble, null, true);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.boundChatWindowResized, {
      passive: true,
    });
  }

  chatWindowResized() {
    if (this.chatElement) {
      this.landscape =
        this.chatElement.offsetWidth > this.chatElement.offsetHeight;
      if (this.waitingForAnimationFrame !== true) {
        this.waitingForAnimationFrame = true;
        window.requestAnimationFrame(() => {
          this.setState({ disableTouchScroll: true }, () => {
            window.requestAnimationFrame(() =>
              this.setState({ disableTouchScroll: false })
            );
          });
          this.chatElement.scrollTop = this.chatElement.scrollHeight;
          this.waitingForAnimationFrame = false;
        });
      }
    }
  }

  captureNextDetailsFromResponse(response) {
    this.hasRemaining = response.remaining > 0;
    this.beforeId = this.hasRemaining
      ? response.messages[response.messages.length - 1].id
      : null;
  }

  injectChatHistoryFromResponse(response) {
    this.captureNextDetailsFromResponse(response);
    let messages = response.messages.reverse();
    let chatHistory = messages.map(item => item.message);
    this.setState({
      conversation: chatHistory.concat(this.state.conversation),
    });
  }

  restore(sessionId) {
    this.botSession = this.botSession || {};
    return this.botSession;
  }

  persist(sessionId, session) {
    this.botSession = session;
  }

  render() {
    let bubbles = this.state.conversation
      .map((item, index) => this.createChatBubbleChip(item, index))
      .flat();

    let endAdornment = (
      <InputAdornment position="end">
        <IconButton
          className="apeInsightsSubmitChat"
          onClick={() => this.issueQuery()}
          onMouseDown={() => this.issueQuery()}
          size="large">
          <ITriangleSide />
        </IconButton>
      </InputAdornment>
    );

    let viewClass = ['chat-view'];
    if (this.props.platform) {
      viewClass.push(`platform-${this.props.platform}`);
    }

    let chatClasses = ['chat'];
    if (this.state.disableTouchScroll === true) {
      chatClasses.push('disable-touch-scroll');
    }

    return (
      <div className={viewClass.join(' ')}>
        <div
          className={chatClasses.join(' ')}
          ref={element => this.captureChatElement(element)}
        >
          {bubbles}
          {this.state.loading === true ? <ILoader fontSize="large" /> : null}
          <div ref={element => (this.bubblesEnd = element)} />
        </div>
        {this.props.embedded ? null : (
          <div className="entry">
            <Input
              id="apeInsightsInput"
              style={{ marginTop: 8 }}
              fullWidth
              multiline
              placeholder="Ask a question…"
              disableUnderline={true}
              endAdornment={endAdornment}
              value={this.state.query || ''}
              onChange={e => this.setState({ query: e.target.value })}
              onKeyUp={e => this.inputKeyUp(e)}
              onKeyPress={e => this.inputKeyPress(e)}
            />
          </div>
        )}
        {this.props.platform == 'android' || this.props.platform == 'uwp' ? (
          <InsightsChatViewEntry
            platform={this.props.platform}
            disableQueryPreview={false}
          />
        ) : null}
      </div>
    );
  }

  captureChatElement(element) {
    this.chatElement = element;
    if (this.chatElement) {
      this.chatElement.addEventListener(
        'scroll',
        () => {
          try {
            if (this.chatElement.scrollTop == 0) {
              if (this.hasRemaining == true && this.loadingRemaining == false) {
                this.loadingRemaining = true;
                apiCallWithOptions(
                  `/site_assistant.json?before_id=${this.beforeId}`,
                  'GET'
                ).then(response => {
                  this.injectChatHistoryFromResponse(response);
                  this.loadingRemaining = false;
                });
              }
            }
          } catch (e) {
            /*dont allow error here to mess with scrolling */
          }
        },
        false
      );
      this.landscape =
        this.chatElement.offsetWidth > this.chatElement.offsetHeight;
    }
  }

  inputKeyUp(e) {
    if (e.key == 'ArrowUp') {
      //load previous query
    }
  }

  inputKeyPress(e) {
    if (e.key == 'Enter') {
      this.issueQuery();
      e.preventDefault();
    }
  }

  createChatBubbleChip(item, index) {
    let label = null;
    if (item.text) {
      label = (
        <span>
          <ReactMarkdown>{item.text}</ReactMarkdown>
        </span>
      );
    }

    if (item.owner == 'system') {
      return [
        <div key={index} className={`chip ${item.owner}-chip`}>
          <div className={'content'} style={{ minWidth: '1px' }}>
            {label}
          </div>
        </div>,
      ];
    }

    let offset = 1;
    let lastItem = null;
    do {
      lastItem = this.state.conversation[
        this.state.conversation.length - offset
      ];
      offset = offset + 1;
    } while (
      lastItem.owner == 'system' &&
      offset <= this.state.conversation.length
    );
    let userActions = <div className={'actions'} />;
    let botActions = <div className={'actions'} />;

    let chatItemActions =
      item.actions && lastItem.correlation_id == item.correlation_id
        ? item.actions.filter(action => action.type == 'chat')
        : null;
    if (chatItemActions && chatItemActions.length > 0) {
      let botActionMenuItems = [];
      if (this.state.actionsOpen == index) {
        botActionMenuItems = this.createChatBubbleActionMenu(
          chatItemActions,
          item
        );
      }
      let paperProps = {
        style: {
          maxHeight: this.landscape == true ? '80%' : '50%',
          minWidth: '300px',
          display: 'block',
        },
      };
      botActions = (
        <div className={'actions'}>
          <IconButton
            aria-label="Actions"
            className="hover"
            onClick={e =>
              this.setState({
                actionsOpen: index,
                anchor: e.target,
              })
            }
            size="large">
            <IPreferences />
          </IconButton>
          {this.props.embedded === true ? null : (
            <Menu
              open={
                this.state.actionsOpen == index && this.state.anchor != null
              }
              action={actions => (this.menuActions = actions)}
              anchorEl={this.state.anchor}
              onClose={() =>
                this.setState({
                  actionsOpen: null,
                  actionGroupsOpen: {},
                })
              }
              PaperProps={paperProps}
            >
              {botActionMenuItems}
            </Menu>
          )}
          {this.props.embedded !== true ? null : (
            <Drawer
              anchor="bottom"
              PaperProps={paperProps}
              open={
                this.state.actionsOpen == index && this.state.anchor != null
              }
              onClose={() =>
                this.setState({
                  actionsOpen: null,
                  actionGroupsOpen: {},
                })
              }
            >
              {botActionMenuItems}
            </Drawer>
          )}
        </div>
      );
    }

    let chips = [
      <div key={index} className={`chip ${item.owner}-chip`}>
        {item.owner == 'user' ? userActions : null}
        <div className={'content'} style={{ minWidth: '1px' }}>
          {label ? <Chip label={label} /> : null}
          {item.image_uri ? (
            <img
              src={item.image_uri}
              style={{ width: '283px', height: '141px' }}
            />
          ) : null}
        </div>
        {item.owner == 'user' ? null : botActions}
      </div>,
    ];
    return chips;
  }

  createChatBubbleActionMenu(actions, item) {
    let groupBy = (xs, key) => {
      return xs.reduce((rv, x) => {
        var v = key instanceof Function ? key(x) : x[key];
        (rv[v] = rv[v] || []).push(x);
        return rv;
      }, {});
    };
    let actionsWithNoGroup = actions.filter(action => action.group == null);
    let actionsWithNoGroupMenuItems = actionsWithNoGroup.map(action =>
      this.createChatBubbleActionMenuItem(action, item)
    );
    let actionsWithGroup = actions.filter(
      action => !actionsWithNoGroup.includes(action)
    );
    let groupedActions = groupBy(actionsWithGroup, item => item.group);
    let actionsWithGroupMenuItems = Object.keys(groupedActions).map(
      actionGroupKey => {
        let actionGroup = groupedActions[actionGroupKey];
        return this.createChatBubbleActionMenuItemGroup(
          actionGroupKey,
          actionGroup,
          item
        );
      }
    );
    return actionsWithNoGroupMenuItems.concat(actionsWithGroupMenuItems.flat());
  }

  createChatBubbleActionMenuItem(action, item) {
    return (
      <MenuItem
        onClick={() => this.chatBubbleActionClick(action, item)}
        key={action.label}
        style={{ minHeight: '24px' }}
      >
        {action.label}
      </MenuItem>
    );
  }

  createChatBubbleActionMenuItemGroup(group, actions, item) {
    let action = actions[0];
    let isOpen =
      this.state.actionGroupsOpen[group] == undefined
        ? false
        : this.state.actionGroupsOpen[group];
    let toggleMenuGroup = () => {
      this.setState(
        {
          actionGroupsOpen: {
            ...this.state.actionGroupsOpen,
            [group]: !isOpen,
          },
        },
        () => {
          if (this.menuActions) this.menuActions.updatePosition();
        }
      );
    };
    return [
      <MenuItem
        key={group}
        onClick={toggleMenuGroup}
        key={group}
        style={{ minHeight: '24px' }}
        component="div"
      >
        {group}
        {isOpen ? <IChevronUp /> : <IChevronDown />}
      </MenuItem>,
      <Collapse
        key={`${group}items`}
        in={isOpen}
        timeout="auto"
        style={{ flexShrink: '0' }}
        unmountOnExit
      >
        <List component="div" disablePadding style={{ paddingLeft: '8px' }}>
          {actions.map(action => (
            <ListItemButton
              onClick={() => this.chatBubbleActionClick(action, item)}
              key={action.label}>
              {action.label}
            </ListItemButton>
          ))}
        </List>
      </Collapse>,
    ];
  }

  chatBubbleActionClick(action, item) {
    this.setState({ actionsOpen: null, actionGroupsOpen: {} }, () => {
      if (action.type == 'chat') {
        this.issueQueryTextFromKeyboard(action.data, item.correlation_id);
      }
    });
  }

  addConversationBubble(bubbles, scroll, clearTemp, callback) {
    clearTemp = clearTemp || bubbles.temp !== true;
    let stateUpdate = {
      conversation: this.state.conversation.filter(
        item => !clearTemp || item.temp !== true
      ),
    };
    if (bubbles.length > 0) {
      bubbles.forEach(bubble => stateUpdate.conversation.push(bubble));
    } else {
      stateUpdate.conversation.push(bubbles);
    }
    this.setState(stateUpdate, () => {
      this.bubblesEnd.scrollIntoView({ behavior: scroll || 'smooth' });
      setTimeout(
        () =>
          this.bubblesEnd.scrollIntoView({
            behavior: scroll || 'smooth',
          }),
        100
      );
      setTimeout(
        () =>
          this.bubblesEnd.scrollIntoView({
            behavior: scroll || 'smooth',
          }),
        200
      );
      if (callback) {
        callback();
      }
    });
  }

  issueQuery() {
    if (!this.state.query || this.state.query == '') {
      return;
    }
    this.issueQueryText(this.state.query);

    //clear the query
    this.setState({ query: '' });
  }

  issueQueryText(queryText, correlation_id) {
    if (!queryText || queryText == '') {
      return;
    }
    let query = bot.preprocessChatPhrase(this.chatSessionId, queryText);
    this.issueQueryTextFromKeyboard(query, correlation_id);
  }

  issueQueryTextFromKeyboard(query, correlation_id) {
    if (!query || query == '') {
      return;
    }

    console.log('correlation_id', correlation_id);
    let tempBubble = {
      owner: 'user',
      text: query,
      correlation_id: correlation_id,
      temp: true,
    };
    this.addConversationBubble(tempBubble);

    this.setState({ loading: true });
    bot
      .processChatPhrase(this.chatSessionId, query, correlation_id)
      .then(bubbles => {
        let promiseChain = Promise.resolve(true);
        bubbles.forEach(bubble => {
          promiseChain = promiseChain.then(_ => {
            if (bubble.image_uri) {
              return apiCallWithOptions(bubble.image_uri, 'GET', {
                responseOnly: true,
              })
                .then(response => response.blob())
                .then(imgblob =>
                  this.addConversationBubble(
                    Object.assign(bubble, {
                      image_uri: URL.createObjectURL(imgblob),
                    })
                  )
                );
            }
            this.addConversationBubble(bubble);
            return Promise.resolve(bubble);
          });
        });
        promiseChain.then(_ => this.setState({ loading: false }));
      })
      .catch(_ => {
        console.log(_);
        this.addConversationBubble({
          owner: 'bot',
          text: t(
            "Oops looks like the server isn't available right now. Please try again in a minute"
          ),
        });
        this.setState({ loading: false });
      });
  }
}
